import { useEffect, useRef, useState } from 'react';
import { attendancesService } from '../services/attendances';
import { notificationSocket } from '../lib/notificationSocket';
import type { NotificationItem } from '../services/notifications';
import { useAuth } from './useAuth';

const POLL_INTERVAL_MS = 30_000;

/**
 * Contagem de atendimentos com mensagem do contato ainda não vista por
 * nenhum operador (inbox compartilhada — ver unread_count() no backend).
 *
 * Além do número (usado como badge na sidebar), dispara uma notificação do
 * navegador quando o total sobe enquanto a aba está em background — objetivo
 * é reduzir a dependência do WhatsApp do celular só pra saber que chegou
 * mensagem nova.
 *
 * O poll de 30s continua como rede de segurança, mas uma notificação
 * `session_needs_human` (ver AppLayout.tsx/notificationSocket.ts, conectado
 * globalmente lá — este hook só assina, nunca conecta/desconecta) dispara um
 * refetch imediato, pro badge não esperar até 30s pra refletir uma
 * escalação que acabou de acontecer.
 */
export function useAttendanceUnreadCount(): number {
  const [count, setCount] = useState(0);
  const { user } = useAuth();
  const prevCountRef = useRef(0);
  const isFirstFetchRef = useRef(true);

  useEffect(() => {
    // is_staff (admin Eleve.IA) não tem escola vinculada e não usa Atendimentos
    // (removido do sidebar do admin) — sem isso, pollava um endpoint escolar à toa.
    if (!user || user.is_staff) return;

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {
        // silencioso — sem permissão, só o badge continua funcionando
      });
    }

    let cancelled = false;

    const fetchCount = async () => {
      try {
        const { total } = await attendancesService.getUnreadCount();
        if (cancelled) return;

        if (
          !isFirstFetchRef.current &&
          total > prevCountRef.current &&
          document.hidden &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          new Notification('Nova mensagem no atendimento', {
            body:
              total === 1
                ? '1 atendimento aguardando resposta.'
                : `${total} atendimentos aguardando resposta.`,
            tag: 'sophia-attendance-unread',
          });
        }

        prevCountRef.current = total;
        isFirstFetchRef.current = false;
        setCount(total);
      } catch {
        // silencioso — badge não é crítico
      }
    };

    fetchCount();
    // Aba em segundo plano não consulta (o WS de notificações ainda dispara fetchCount).
    const interval = setInterval(() => { if (!document.hidden) fetchCount(); }, POLL_INTERVAL_MS);

    const onNotification = (n: NotificationItem) => {
      if (n.notification_type === 'session_needs_human') fetchCount();
    };
    notificationSocket.on('notification.new', onNotification);

    return () => {
      cancelled = true;
      clearInterval(interval);
      notificationSocket.off('notification.new', onNotification);
    };
  }, [user]);

  return count;
}
